import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Grid, MenuItem, ToggleButton, ToggleButtonGroup, Slider, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { app, getGraphicMode, getLastGraphicMode, getTransparencyOpacity } from 'store';
import { GraphicMode } from 'types';
import GVSwitch from 'components/lib/GVSwitch/GVSwitch';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import { debounce } from 'lodash';
import { getStyleVariables } from 'styles/vars';
import styles from './GraphicModeControl.module.css';
import { persistDisplaySetting } from 'store/app/persistDisplaySetting';

interface GraphicModeControlProps {
  loaded: boolean | undefined;
}

const useStyles = makeStyles()((theme) => ({
  menuItem: {
    minHeight: 'fit-content !important',
  },
  graphicInfoIcon: {
    fontSize: '16px',
    marginLeft: '4px',
  },
  graphicInfoSlot: {
    display: 'flex',
    alignItems: 'center',
  },
  graphicRow: {
    marginLeft: 0,
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    rowGap: '8px',
  },
  graphicToggleRow: {
    minHeight: '28px',
  },
  graphicSwitch: {
    marginRight: 4,
    marginLeft: 0,
  },
  graphicIndicator: {
    position: 'absolute',
    top: '1px',
    height: 'calc(100% - 2px)',
    left: 0,
    width: 'var(--indicatorWidth, 0px)',
    transform: 'translateX(var(--tx, 0px))',
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '6px',
    transition: 'transform 220ms ease, background-color 180ms ease',
    pointerEvents: 'none',
    zIndex: 0,
  },
  graphicIndicatorDisabled: {
    backgroundColor: 'rgba(255,255,255,0.16)',
  },
  graphicGroup: {
    backgroundColor: getStyleVariables(theme).global.darkBorder,
    borderRadius: '6px',
    padding: '1px 0px',
    marginLeft: '2px',
    overflow: 'hidden',
    position: 'relative',
    '& .MuiToggleButtonGroup-grouped': {
      borderRadius: '6px',
      border: '0 !important',
      margin: 0,
      '&:not(:first-of-type)': {
        marginLeft: 0,
        borderLeft: '0 !important',
      },
    },
    '& .MuiToggleButton-root': {
      position: 'relative',
      zIndex: 1,
      textTransform: 'none',
      color: '#FFFFFF',
      backgroundColor: 'transparent',
      padding: '0px 8px',
      minHeight: '20px',
      lineHeight: '16px',
      fontSize: '11px',
      fontWeight: 600,
      minWidth: 'unset',
      '&.Mui-selected': {
        borderRadius: '6px',
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& .MuiToggleButton-root': {
        transition: 'none',
      },
    },
  },
  sliderRow: {
    width: '100%',
    paddingLeft: '34px',
  },
  sliderWrapper: {
    flex: 1,
    minWidth: 0,
    marginRight: '8px',
  },
  opacitySlider: {
    color: theme.palette.secondary.main,
    height: '3px',
    padding: '6.5px 0',
    '& .MuiSlider-thumb': {
      backgroundColor: theme.palette.secondary.main,
      '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
        boxShadow: 'inherit',
      },
      '&::before': {
        display: 'none',
      },
    },
    '& .MuiSlider-track': {
      border: 'none',
    },
    '& .MuiSlider-rail': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      opacity: 1,
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
      '& .MuiSlider-rail': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
  percentageLabel: {
    fontSize: '10px',
    lineHeight: '16px',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 600,
    minWidth: '28px',
    textAlign: 'center',
  },
}));

const GraphicModeControl = (props: GraphicModeControlProps) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const graphicMode = useSelector(getGraphicMode);
  const lastGraphicMode = useSelector(getLastGraphicMode);
  const transparencyOpacity = useSelector(getTransparencyOpacity);
  const [sliderValue, setSliderValue] = useState(transparencyOpacity);

  const setGraphicMode = (mode: GraphicMode) => {
    dispatch(app.actions.setGraphicMode(mode));
    if (mode === 'flashing' || mode === 'transparentOverlay') {
      dispatch(app.actions.setLastGraphicMode(mode));
    }
  };

  // Create debounced opacity update (300ms delay for smooth real-time updates)
  const debouncedOpacityUpdate = useRef(
    debounce((value: number) => {
      dispatch(app.actions.setTransparencyOpacity(value));
    }, 300),
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedOpacityUpdate.cancel();
    };
  }, [debouncedOpacityUpdate]);

  // Sync slider value when transparency opacity changes from Redux
  useEffect(() => {
    setSliderValue(transparencyOpacity);
  }, [transparencyOpacity]);

  const isGraphicEffectOn = graphicMode !== '';
  const selectedGraphicMode: GraphicMode = graphicMode || lastGraphicMode;
  const isGraphicGroupDisabled = !isGraphicEffectOn || !props.loaded;

  useLayoutEffect(() => {
    const el = document.getElementById('graphicModeGroup') as HTMLElement | null;
    if (!el) return;

    let frameId: number | null = null;

    const setIndicator = () => {
      const selectedButton = el.querySelector('[aria-pressed="true"]') as HTMLElement | null;
      if (!selectedButton) return;
      el.style.setProperty('--indicatorWidth', selectedButton.offsetWidth + 'px');
      el.style.setProperty('--tx', selectedButton.offsetLeft + 'px');
    };

    // Measure options on open and mode change
    // Use double rAF to ensure DOM is fully painted
    frameId = requestAnimationFrame(() => {
      frameId = requestAnimationFrame(setIndicator);
    });

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [selectedGraphicMode]);

  const handleGraphicEffectSwitch = () => {
    const nextOn = !isGraphicEffectOn;
    setGraphicMode(nextOn ? lastGraphicMode : '');
    persistDisplaySetting('graphicEffectOn', nextOn);
  };

  const handleGraphicModeSelection = (_e: React.MouseEvent<HTMLElement>, newMode: GraphicMode) => {
    if (!newMode || newMode === graphicMode) return;
    setGraphicMode(newMode);
    if (newMode === 'flashing' || newMode === 'transparentOverlay') {
      persistDisplaySetting('graphicMode', newMode);
    }
  };

  const handleOpacityChange = (_event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setSliderValue(value);
    // Debounced update while dragging
    debouncedOpacityUpdate(value);
  };

  const handleOpacityChangeCommitted = (_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    // Cancel any pending debounced update
    debouncedOpacityUpdate.cancel();
    dispatch(app.actions.setTransparencyOpacity(value));
    persistDisplaySetting('transparencyOpacity', value);
  };

  return (
    <MenuItem data-testid="cog_graphic_effect" className={classes.menuItem}>
      <Grid
        container
        direction="column"
        alignItems="flex-start"
        wrap="nowrap"
        columnGap={0}
        className={classes.graphicRow}
      >
        <Grid
          item
          container
          direction="row"
          flexWrap={'nowrap'}
          alignItems="center"
          className={classes.graphicToggleRow}
        >
          <Grid item>
            <GVSwitch
              color="secondary"
              checked={isGraphicEffectOn}
              onChange={handleGraphicEffectSwitch}
              disabled={!props.loaded}
              className={classes.graphicSwitch}
            />
          </Grid>
          <Grid item>
            <ToggleButtonGroup
              exclusive
              value={selectedGraphicMode}
              onChange={handleGraphicModeSelection}
              aria-label="Graphic display mode"
              disabled={!isGraphicEffectOn || !props.loaded}
              className={classes.graphicGroup}
              id="graphicModeGroup"
              size="small"
            >
              <span
                className={cx(classes.graphicIndicator, {
                  [classes.graphicIndicatorDisabled]: isGraphicGroupDisabled,
                })}
              />
              <ToggleButton
                value="flashing"
                aria-label="Flash"
                data-testid="graphic_mode_flashing"
                disableRipple
                disableFocusRipple
                disableTouchRipple
              >
                Flash
              </ToggleButton>
              <ToggleButton
                value="transparentOverlay"
                aria-label="Transparency"
                data-testid="graphic_mode_transparency"
                disableRipple
                disableFocusRipple
                disableTouchRipple
              >
                Transparency
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item className={classes.graphicInfoSlot}>
            <GVTooltip
              title={
                <>
                  Controls how differences are displayed. Choose <strong>Flash</strong> to alternate areas of change for
                  more visual contrast, or <strong>Transparency</strong> to overlay graphics for a clearer side-by-side
                  comparison.
                </>
              }
              side="bottom"
              align="start"
              sideOffset={16}
              className={styles.tooltip}
            >
              <InfoOutlinedIcon className={classes.graphicInfoIcon} />
            </GVTooltip>
          </Grid>
        </Grid>
        {graphicMode === 'transparentOverlay' && (
          <Grid item container direction="row" alignItems="center" wrap="nowrap" className={classes.sliderRow}>
            <Grid item className={classes.sliderWrapper}>
              <Slider
                value={sliderValue}
                onChange={handleOpacityChange}
                onChangeCommitted={handleOpacityChangeCommitted}
                min={0.1}
                max={1.0}
                step={0.05}
                disabled={!props.loaded}
                size="small"
                data-testid="cog_transparency_opacity_slider"
                className={classes.opacitySlider}
              />
            </Grid>
            <Grid item>
              <Typography variant="body2" className={classes.percentageLabel}>
                {Math.round(sliderValue * 100)}%
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </MenuItem>
  );
};

export default GraphicModeControl;
